// Theme definitions for TaskPriority Prime

export const themes = {
  prime: {
    name: 'Prime',
    font: 'font-sans',
    colors: {
      // Backgrounds
      body: 'bg-slate-50 dark:bg-black',
      card: 'bg-white dark:bg-gray-900',
      categoryA: 'border-red-600 bg-red-100 dark:bg-red-950',
      categoryB: 'border-blue-600 bg-blue-100 dark:bg-blue-950',
      categoryC: 'border-green-600 bg-green-100 dark:bg-green-950',
      
      // Headers
      headerBg: 'bg-white dark:bg-gray-900',
      headerBorder: 'border-slate-200 dark:border-gray-700',
      
      // Category headers
      categoryAHeader: 'bg-red-200 dark:bg-red-900',
      categoryBHeader: 'bg-blue-200 dark:bg-blue-900',
      categoryCHeader: 'bg-green-200 dark:bg-green-900',
      
      // Text colors
      categoryAText: 'text-red-900 dark:text-red-100',
      categoryBText: 'text-blue-900 dark:text-blue-100',
      categoryCText: 'text-green-900 dark:text-green-100',
      
      primaryText: 'text-slate-900 dark:text-white',
      secondaryText: 'text-slate-500 dark:text-gray-400',
      
      // Journal
      journal: 'bg-white dark:bg-gray-200 border-black dark:border-gray-700',
      journalText: 'text-black dark:text-black',
    }
  },
  
  inkpaper: {
    name: 'Ink & Paper',
    font: 'font-inkfree', // Uses Caveat font
    colors: {
      // Backgrounds - Off-white/cream for outer areas
      body: 'bg-[#FAF8F3] dark:bg-black',
      card: 'bg-[#FAF8F3] dark:bg-black',
      
      // Category backgrounds - Different cream shades with notebook lines
      categoryA: 'border-[#1e3a5f] bg-[#FFFEF8] dark:bg-[#FFFEF8]',
      categoryB: 'border-[#1e3a5f] bg-[#FFF9E6] dark:bg-[#FFF9E6]',
      categoryC: 'border-[#1e3a5f] bg-[#FFF4D6] dark:bg-[#FFF4D6]',
      
      // Headers - Slightly darker cream, same for all
      headerBg: 'bg-[#FAF8F3] dark:bg-black',
      headerBorder: 'border-[#1e3a5f] dark:border-[#1e3a5f]',
      
      // Category headers - Same cream tone for all
      categoryAHeader: 'bg-[#F5F0E8] dark:bg-[#F5F0E8]',
      categoryBHeader: 'bg-[#F5F0E8] dark:bg-[#F5F0E8]',
      categoryCHeader: 'bg-[#F5F0E8] dark:bg-[#F5F0E8]',
      
      // Text colors - All navy blue (ink pen color)
      categoryAText: 'text-[#1e3a5f] dark:text-[#1e3a5f]',
      categoryBText: 'text-[#1e3a5f] dark:text-[#1e3a5f]',
      categoryCText: 'text-[#1e3a5f] dark:text-[#1e3a5f]',
      
      primaryText: 'text-[#1e3a5f] dark:text-[#1e3a5f]',
      secondaryText: 'text-[#4a5568] dark:text-[#4a5568]',
      
      // Journal - Paper with lines
      journal: 'bg-[#FFFEF8] dark:bg-[#FFFEF8] border-[#1e3a5f] dark:border-[#1e3a5f]',
      journalText: 'text-[#1e3a5f] dark:text-[#1e3a5f]',
    }
  },
  
  executive: {
    name: 'Executive',
    font: 'font-executive-body', // Open Sans for body text
    headingFont: 'font-executive-heading', // Cinzel for headings - UPPERCASE
    colors: {
      // Backgrounds - Deep Navy Blue
      body: 'bg-[#142840] dark:bg-[#060E1A]',
      card: 'bg-[#1A3458] dark:bg-[#0A1C34]',
      
      // Category backgrounds - Distinct gray shades
      categoryA: 'border-[#DAA520] bg-[#C8C8C8] dark:bg-[#A0A0A0]',
      categoryB: 'border-[#DAA520] bg-[#B8B8B8] dark:bg-[#909090]',
      categoryC: 'border-[#DAA520] bg-[#A8A8A8] dark:bg-[#808080]',
      
      // Headers - Navy blue
      headerBg: 'bg-[#1A3458] dark:bg-[#0A1C34]',
      headerBorder: 'border-[#DAA520] dark:border-[#DAA520]',
      
      // Category headers - Medium gray
      categoryAHeader: 'bg-[#B0B0B0] dark:bg-[#888888]',
      categoryBHeader: 'bg-[#B0B0B0] dark:bg-[#888888]',
      categoryCHeader: 'bg-[#B0B0B0] dark:bg-[#888888]',
      
      // Text colors - Dark for cards, light for header
      categoryAText: 'text-[#0D2847] dark:text-[#1A2A3A]',
      categoryBText: 'text-[#0D2847] dark:text-[#1A2A3A]',
      categoryCText: 'text-[#0D2847] dark:text-[#1A2A3A]',
      
      primaryText: 'text-[#C9D6E8] dark:text-[#C9D6E8]',
      secondaryText: 'text-[#8FA8C8] dark:text-[#8FA8C8]',
      
      // Gold accent color for special elements - Classic Gold
      accentGold: 'text-[#DAA520]',
      accentGoldBg: 'bg-[#DAA520]',
      accentGoldBorder: 'border-[#DAA520]',
      
      // Journal - Medium gray with classic gold border
      journal: 'bg-[#C0C0C0] dark:bg-[#909090] border-[#DAA520] dark:border-[#DAA520]',
      journalText: 'text-[#0D2847] dark:text-[#1A2A3A]',
    }
  },

  frostedglass: {
    name: 'Frosted Glass',
    font: 'font-frosted',
    colors: {
      // Backgrounds - Vivid gradient so glass has something to blur against
      body: 'frosted-body',
      card: 'frosted-card',

      // Category backgrounds - Glass panels with Prime category tints
      categoryA: 'border-red-400/50 frosted-panel-red',
      categoryB: 'border-blue-400/50 frosted-panel-blue',
      categoryC: 'border-green-400/50 frosted-panel-green',

      // Headers - Glass header
      headerBg: 'frosted-header',
      headerBorder: 'border-white/20 dark:border-white/10',

      // Category headers - Subtle tinted glass
      categoryAHeader: 'frosted-badge-red',
      categoryBHeader: 'frosted-badge-blue',
      categoryCHeader: 'frosted-badge-green',

      // Text colors - Same as Prime
      categoryAText: 'text-red-900 dark:text-red-200',
      categoryBText: 'text-blue-900 dark:text-blue-200',
      categoryCText: 'text-green-900 dark:text-green-200',

      primaryText: 'text-slate-800 dark:text-white',
      secondaryText: 'text-slate-500 dark:text-gray-400',

      // Journal - Frosted glass journal
      journal: 'frosted-journal border-white/30 dark:border-white/10',
      journalText: 'text-slate-800 dark:text-white',
    }
  },

  forest: {
    name: 'Forest Focus',
    font: 'font-forest',
    colors: {
      // Backgrounds - Soft sage/moss
      body: 'bg-[#e8ede5] dark:bg-[#1a2418]',
      card: 'bg-[#f0f4ed] dark:bg-[#222e1f]',

      // Category backgrounds - Earth tones
      categoryA: 'border-[#2d5a3e] bg-[#c8d9ce] dark:bg-[#1c2e22]',
      categoryB: 'border-[#4a7c59] bg-[#e4f0e8] dark:bg-[#243a2c]',
      categoryC: 'border-[#b8943f] bg-[#f5f0e0] dark:bg-[#332d1a]',

      // Headers
      headerBg: 'bg-[#2d4a35] dark:bg-[#1a2e1f]',
      headerBorder: 'border-[#3d6b4a] dark:border-[#3d6b4a]',

      // Category headers
      categoryAHeader: 'bg-[#a8c4ae] dark:bg-[#2a4a32]',
      categoryBHeader: 'bg-[#b8d4c0] dark:bg-[#2a4a32]',
      categoryCHeader: 'bg-[#e8ddb8] dark:bg-[#4a4020]',

      // Text colors
      categoryAText: 'text-[#1a3e28] dark:text-[#a8d4b4]',
      categoryBText: 'text-[#1e4a2a] dark:text-[#a8d4b8]',
      categoryCText: 'text-[#5a4a1a] dark:text-[#e0d4a8]',

      primaryText: 'text-[#f0ede8] dark:text-[#d8e0d4]',
      secondaryText: 'text-[#b8c4b0] dark:text-[#7a8a74]',

      // Journal
      journal: 'bg-[#f5f0e8] dark:bg-[#222e1f] border-[#4a7c59] dark:border-[#3d6b4a]',
      journalText: 'text-[#2a3a28] dark:text-[#c8d4c0]',
    }
  },

  ocean: {
    name: 'Ocean Depth',
    font: 'font-ocean',
    colors: {
      // Backgrounds - Deep ocean
      body: 'bg-[#b8d0e0] dark:bg-[#0a1929]',
      card: 'bg-[#c4d8e8] dark:bg-[#0f2440]',

      // Category backgrounds - Deep blue to teal spectrum
      categoryA: 'border-[#1a4a6e] bg-[#9cb8d4] dark:bg-[#122a44]',
      categoryB: 'border-[#1a6080] bg-[#a0c4d8] dark:bg-[#0e3838]',
      categoryC: 'border-[#3a8a9a] bg-[#a8d4e0] dark:bg-[#143840]',

      // Headers - Deep navy
      headerBg: 'bg-[#0d2b4a] dark:bg-[#081c33]',
      headerBorder: 'border-[#1a4a6e] dark:border-[#1a4a6e]',

      // Category headers
      categoryAHeader: 'bg-[#a0bcd4] dark:bg-[#1a3a5e]',
      categoryBHeader: 'bg-[#88b8cc] dark:bg-[#163a3a]',
      categoryCHeader: 'bg-[#a8dce4] dark:bg-[#1a3e44]',

      // Text colors
      categoryAText: 'text-[#0d2b4a] dark:text-[#a8c8e8]',
      categoryBText: 'text-[#0a3a5a] dark:text-[#88d4d4]',
      categoryCText: 'text-[#1a5a68] dark:text-[#a0dce8]',

      primaryText: 'text-[#e8f0f4] dark:text-[#c8dce8]',
      secondaryText: 'text-[#8ab0c8] dark:text-[#5a8aaa]',

      // Journal
      journal: 'bg-[#eaf3f8] dark:bg-[#0f2440] border-[#1a7a7a] dark:border-[#1a5a5a]',
      journalText: 'text-[#0d2b4a] dark:text-[#b0d0e0]',
    }
  },

  floral: {
    name: 'Spring Floral',
    font: 'font-floral',
    colors: {
      // Backgrounds - Soft blush/cream with floral background
      body: 'floral-bg',
      card: 'bg-[#fef7f8]/90 dark:bg-[#32222a]/90',

      // Category backgrounds - Rose, Lavender, Sage
      categoryA: 'border-[#c45a78] bg-[#fce0e6] dark:bg-[#3a2028]',
      categoryB: 'border-[#8a6aaa] bg-[#ede0f4] dark:bg-[#2e2238]',
      categoryC: 'border-[#6a9a6a] bg-[#e0f0e4] dark:bg-[#1e2e22]',

      // Headers - Deep rose
      headerBg: 'bg-[#8a3050] dark:bg-[#4a1830]',
      headerBorder: 'border-[#c45a78] dark:border-[#8a3050]',

      // Category headers
      categoryAHeader: 'bg-[#f0b8c8] dark:bg-[#5a2838]',
      categoryBHeader: 'bg-[#d0b8e0] dark:bg-[#3e2850]',
      categoryCHeader: 'bg-[#b8d8bc] dark:bg-[#284030]',

      // Text colors
      categoryAText: 'text-[#6a2040] dark:text-[#f0c0d0]',
      categoryBText: 'text-[#4a2868] dark:text-[#d0b8e8]',
      categoryCText: 'text-[#2a5a30] dark:text-[#a8d4ac]',

      primaryText: 'text-[#f8eff2] dark:text-[#e8d4dc]',
      secondaryText: 'text-[#c8a0b0] dark:text-[#8a6878]',

      // Journal
      journal: 'bg-[#fef7f8] dark:bg-[#32222a] border-[#c45a78] dark:border-[#8a3050]',
      journalText: 'text-[#4a2040] dark:text-[#e0c0d0]',
    }
  },

  sunset: {
    name: 'Sunset',
    font: 'font-sunset',
    colors: {
      // Backgrounds - Warm sunset orange with glow
      body: 'sunset-bg',
      card: 'bg-[#f0bc78]/90 dark:bg-[#342018]/90',

      // Category backgrounds - Bold sunset spectrum
      categoryA: 'border-[#c41a0a] bg-[#dc6850] dark:bg-[#4e1a10]',
      categoryB: 'border-[#e06010] bg-[#f09030] dark:bg-[#5a3010]',
      categoryC: 'border-[#c89800] bg-[#e8c020] dark:bg-[#4a3808]',

      // Headers - Deep burnt red
      headerBg: 'bg-[#8b2a1a] dark:bg-[#4a1810]',
      headerBorder: 'border-[#e86020] dark:border-[#d42b1a]',

      // Category headers
      categoryAHeader: 'bg-[#c85040] dark:bg-[#6a2818]',
      categoryBHeader: 'bg-[#e07820] dark:bg-[#6a4010]',
      categoryCHeader: 'bg-[#d4a810] dark:bg-[#584810]',

      // Text colors
      categoryAText: 'text-[#5a0a04] dark:text-[#f8c8c0]',
      categoryBText: 'text-[#4a1a04] dark:text-[#f8d8b0]',
      categoryCText: 'text-[#3a2800] dark:text-[#f0e098]',

      primaryText: 'text-[#fae8d8] dark:text-[#e8d0c0]',
      secondaryText: 'text-[#d4a888] dark:text-[#8a6848]',

      // Journal - with notebook lines
      journal: 'bg-[#f8e0c4] dark:bg-[#342018] border-[#e86020] dark:border-[#d42b1a]',
      journalText: 'text-[#4a2010] dark:text-[#e0c8b0]',
    }
  }
};

export const getTheme = (themeName) => {
  return themes[themeName] || themes.prime;
};
